import { motion } from 'framer-motion';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

interface CheckInCard {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  title: string;
  description: string;
}

const CHECK_IN_CARDS: CheckInCard[] = [
  {
    icon: SunIcon,
    label: 'Morning',
    title: 'Morning check-in',
    description:
      'Start each day with intention. Log your mood, gratitude, and what you\u2019re bringing to today.',
  },
  {
    icon: MoonIcon,
    label: 'Nightly',
    title: 'Nightly review',
    description:
      'End each day with honesty. Review what happened and where your spiritual condition stood.',
  },
];

const FEATURE_PILLS = [
  'Push notification reminders',
  'Streak tracking',
  'AI analysis (Premium)',
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14 } },
};

export function DailyPracticeSection() {
  return (
    <section
      id="daily"
      className="bg-[#0D1321] py-20 px-4"
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            A daily spiritual practice, built into your routine.
          </h2>
        </motion.div>

        {/* Big Book quote */}
        <motion.blockquote
          className="border-l-4 border-teal-400 pl-6 pr-4 py-2 mb-14 max-w-2xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={fadeUp}
        >
          <p className="font-serif italic text-lg text-slate-300 leading-relaxed mb-2">
            &ldquo;What we really have is a daily reprieve contingent on the
            maintenance of our spiritual condition.&rdquo;
          </p>
          <cite className="text-sm text-slate-400 not-italic font-medium">
            Big Book, p. 85
          </cite>
        </motion.blockquote>

        {/* Check-in cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {CHECK_IN_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.title}
                variants={fadeUp}
                className="rounded-2xl bg-white/5 border border-white/10 p-8 flex flex-col gap-4"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-teal-500/15">
                    <Icon className="w-5 h-5 text-teal-400" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-teal-400">
                    {card.label}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {card.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Feature pills */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-3"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
        >
          {FEATURE_PILLS.map((pill) => (
            <motion.span
              key={pill}
              variants={fadeUp}
              className="bg-teal-500/15 text-teal-300 rounded-full px-4 py-1.5 text-sm font-medium"
            >
              {pill}
            </motion.span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
